import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./login.css";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import {
  APPLICATION_DB_EDITOR,
  getAccessibleApplications,
  getApplicationRoute,
} from "../../utils/authAccess";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [focusedField, setFocusedField] = useState("");
  const navigate = useNavigate();

  // Animation effects
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsFormVisible(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const validateInputs = () => {
    if (!username?.trim() || !password) {
      setError("Username and password are required.");
      return false;
    }
    return true;
  };

  const handleLogin = async () => {
    if (!validateInputs()) return;

    setLoading(true);
    setError("");

    try {
      const data = await fetchWithAuth({
        method: "POST",
        endpoint: "/auth/login",
        headers: { "Content-Type": "application/json" },
        data: { username, password },
      });

      setLoading(false);

      if (data && data.userId) {
        console.log("Login successful:", data);

        const roleIdList = Array.isArray(data.roleIds) ? data.roleIds : [];
        const appRoleIds =
          data.appRoleIds && typeof data.appRoleIds === "object"
            ? data.appRoleIds
            : {};
        const accessibleApplications = getAccessibleApplications(appRoleIds);

        if (!accessibleApplications.length) {
          setError("You do not have access to any application.");
          return;
        }

        const expiresIn = 30 * 60 * 1000;
        const persistCommonAuthState = () => {
          localStorage.setItem("authenticated", "true");
          localStorage.setItem("loginTimestamp", Date.now().toString());
          localStorage.setItem("expiresIn", expiresIn.toString());
          localStorage.setItem("roleId", JSON.stringify(roleIdList));
          localStorage.setItem("appRoleIds", JSON.stringify(appRoleIds));
          localStorage.setItem("username", username);
        };

        if (accessibleApplications.length === 1) {
          const selectedApplication = accessibleApplications[0];
          persistCommonAuthState();

          if (selectedApplication === APPLICATION_DB_EDITOR) {
            const existingUnit = (localStorage.getItem("unitNumber") || "").trim();
            if (existingUnit.length !== 4) {
              localStorage.setItem("application", "");
              localStorage.setItem("unitNumber", "");
              localStorage.setItem("pendingUnitNumber", "");
              navigate("/application-selector");
              return;
            }
          } else {
            localStorage.setItem("unitNumber", "");
          }

          localStorage.setItem("pendingUnitNumber", "");
          localStorage.setItem("application", selectedApplication);
          navigate(getApplicationRoute(selectedApplication));
          return;
        }

        persistCommonAuthState();
        localStorage.setItem("application", "");
        localStorage.setItem("unitNumber", "");
        localStorage.setItem("pendingUnitNumber", "");
        navigate("/application-selector");
      } else {
        const errorMsg =
          typeof data === "string" ? data : data?.message || "Login failed";
        setError(errorMsg);
      }
    } catch (err) {
      setLoading(false);
      console.error("Login error:", err);

      if (err.response?.data) {
        const backendError =
          typeof err.response.data === "string"
            ? err.response.data
            : err.response.data.message || "Login failed";
        setError(backendError);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    }
  };

  const closeErrorPopup = () => setError("");

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !loading) {
      handleLogin();
    }
  };

  return (
    <>
      <div className="topbarContainer">
        <div className="topbarLeft">
          <img
            src="/assets/shs-header-logo.png"
            alt="Logo"
            className="logoImg"
          />
        </div>
        <div className="topbarCenter">
          <span className="appname">DB EDITOR</span>
        </div>
      </div>

      <div className="login">
        {/* Floating background elements */}
        <div className="floating-elements">
          <div className="floating-circle circle-1"></div>
          <div className="floating-circle circle-2"></div>
          <div className="floating-circle circle-3"></div>
          <div className="floating-square square-1"></div>
          <div className="floating-square square-2"></div>
        </div>

        <div className={`loginBox ${isFormVisible ? 'form-visible' : ''}`}>

          <div className="form-group">
            <label className={`input-label ${focusedField === 'username' ? 'focused' : ''}`}>
              User Name
              <input
                type="text"
                className={`loginInput ${focusedField === 'username' ? 'input-focused' : ''}`}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onFocus={() => setFocusedField('username')}
                onBlur={() => setFocusedField('')}
                onKeyDown={handleKeyDown}
                placeholder="Enter your username"
              />
            </label>
          </div>

          <div className="form-group">
            <label className={`input-label ${focusedField === 'password' ? 'focused' : ''}`}>
              Password
              <input
                type="password"
                className={`loginInput ${focusedField === 'password' ? 'input-focused' : ''}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField('')}
                onKeyDown={handleKeyDown}
                placeholder="Enter your password"
              />
            </label>
          </div>

          <button
            className={`loginButton ${loading ? 'loading' : ''}`}
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? (
              <div className="button-loading">
                <div className="spinner"></div>
                <span>Logging in...</span>
              </div>
            ) : (
              "Log In"
            )}
          </button>
        </div>
      </div>

      {loading && (
        <>
          <div className="blur-background"></div>
          <div className="loading"></div>
        </>
      )}

      {error && <div className="blur-overlay"></div>}

      {error && (
        <div className="popup">
          <div className="popup-message">{error}</div>
          <button className="popup-close" onClick={closeErrorPopup}>
            &times;
          </button>
        </div>
      )}
    </>
  );
}
